import React from "react";

import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

/**
 * A mark stands as tall as the type beside it, give or take a sixth: matched to
 * the line box it towers over the word, matched to the cap height it disappears.
 *
 * These are px and not em on purpose. A mark is almost always a sibling of its
 * Typography rather than a child, so the row it sits in carries no font size of
 * its own and an em would resolve against the document's 16px, not against the
 * 12px label an inch away. Pick by the type it stands next to.
 */
export const ICON_SIZE = {
  /** Beside 12px type: labelSMedium, labelXS*, caption, bodyS, buttonS. */
  small: 14,
  /** Beside 14px type: labelRMedium, body2, bodyM, bodyRRegular, button. */
  medium: 16,
  /** Alone in a control, or beside 16px type. */
  control: 18,
} as const;

export const inlineIconSx = { position: "relative", top: "-0.06em" } as const;

// Every asset carries the id Streamline exported it with. Injected once per
// row that draws it, that is a page full of elements sharing one id.
const ID_ATTRIBUTE = /\sid="[^"]*"/;
const cleaned = new Map<string, string>();

const withoutId = (svg: string): string => {
  const hit = cleaned.get(svg);
  if (hit !== undefined) return hit;
  const stripped = svg.replace(ID_ATTRIBUTE, "");
  cleaned.set(svg, stripped);
  return stripped;
};

const hasName = (props: {
  "aria-label"?: string;
  "aria-labelledby"?: string;
}) =>
  props["aria-label"] !== undefined || props["aria-labelledby"] !== undefined;

type StreamlineIconProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "dangerouslySetInnerHTML" | "children"
> & {
  svg: string;
  testId?: string;
  /** Pick from ICON_SIZE by the type this mark stands beside. */
  size?: number;
  sx?: SxProps<Theme>;
};

/**
 * Renders a Streamline SVG imported with `?raw`. The artwork draws in
 * currentColor, so callers set the colour on the button and it follows.
 *
 * Takes a ref and passes on whatever else it is given, because MUI's Tooltip
 * hands its child both a ref and its open/close handlers, and a child that
 * drops them is a control whose tooltip silently never appears.
 *
 * `size` is the box. Most of the set fills that box, but a wide glyph like the
 * eye is drawn short, so it will always read smaller than a bin beside it at
 * the same number. Matching the long axis is the trade: matching the short one
 * would leave the eye wider than everything else.
 */
export const StreamlineIcon = React.forwardRef<
  HTMLSpanElement,
  StreamlineIconProps
>(({ svg, testId, size = ICON_SIZE.control, className, sx, ...rest }, ref) => {
  const box = `${size}px`;
  return (
    <Box
      {...rest}
      ref={ref}
      component="span"
      // Decorative by default, because the control around it carries the name.
      // Where the mark is the name, hiding it would leave nothing to read.
      role={hasName(rest) ? "img" : undefined}
      aria-hidden={hasName(rest) ? undefined : true}
      data-testid={testId}
      className={className}
      sx={[
        {
          display: "inline-flex",
          height: box,
          width: box,
          flexShrink: 0,
          "& svg": { display: "block", height: "100%", width: "100%" },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      dangerouslySetInnerHTML={{ __html: withoutId(svg) }}
    />
  );
});

StreamlineIcon.displayName = "StreamlineIcon";
